import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { GUIDES, getGuide } from "../lib/guides";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import Seo, { SITE_ORIGIN } from "../components/Seo";

export default function GuideArticle() {
  const { slug } = useParams();
  const guide = getGuide(slug);
  const { user, loginWithGoogle } = useAuth();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!guide) return <Navigate to="/guides" replace />;

  const related = GUIDES.filter((g) => g.slug !== slug).slice(0, 2);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: guide.title,
        description: guide.description,
        articleSection: guide.category,
        author: { "@type": "Organization", name: "CardArk" },
        publisher: {
          "@type": "Organization",
          name: "CardArk",
          logo: { "@type": "ImageObject", url: `${SITE_ORIGIN}/icon-192.png` },
        },
        mainEntityOfPage: `${SITE_ORIGIN}/guides/${guide.slug}`,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Guides", item: `${SITE_ORIGIN}/guides` },
          { "@type": "ListItem", position: 2, name: guide.title, item: `${SITE_ORIGIN}/guides/${guide.slug}` },
        ],
      },
      ...(guide.faqs
        ? [{
            "@type": "FAQPage",
            mainEntity: guide.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }]
        : []),
    ],
  };

  return (
    <div className="min-h-screen bg-[#05060f] text-white px-6 py-16">
      <Seo
        title={`${guide.title} — CardArk Guides`}
        description={guide.description}
        path={`/guides/${guide.slug}`}
        type="article"
        jsonLd={jsonLd}
      />
      <article className="max-w-3xl mx-auto">
        <Link to="/guides" data-testid="guide-back-link" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-8 transition-colors w-fit">
          <ArrowLeft className="w-4 h-4" /> All guides
        </Link>

        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 mb-4">
          <span className="px-2 py-0.5 rounded-full border border-[#A78BFA]/30 bg-[#A78BFA]/10 text-[#A78BFA]">{guide.category}</span>
          <span>{guide.readTime}</span>
        </div>
        <h1 data-testid="guide-article-title" className="font-display text-3xl md:text-4xl font-bold leading-tight mb-6">
          {guide.title}
        </h1>
        <p className="text-zinc-300 text-base leading-relaxed mb-10 border-l-2 border-[#A78BFA]/50 pl-4">
          {guide.intro}
        </p>

        <div className="space-y-10">
          {guide.sections.map((section) => (
            <section key={section.heading}>
              <h2 className="font-display text-lg md:text-xl font-semibold mb-3">{section.heading}</h2>
              {section.paragraphs?.map((p) => (
                <p key={p.slice(0, 40)} className="text-sm md:text-base text-zinc-400 leading-relaxed mb-3">
                  {p.split(/(\*\*[^*]+\*\*)/g).map((chunk, i) =>
                    chunk.startsWith("**") && chunk.endsWith("**")
                      ? <strong key={i} className="text-white font-medium">{chunk.slice(2, -2)}</strong>
                      : <span key={i}>{chunk}</span>
                  )}
                </p>
              ))}
              {section.list && (
                <ul className="space-y-2 mt-2">
                  {section.list.map((item) => (
                    <li key={item.slice(0, 40)} className="text-sm md:text-base text-zinc-400 leading-relaxed flex gap-3">
                      <span className="text-[#A78BFA] mt-1 shrink-0">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        {guide.faqs && (
          <div data-testid="guide-faq-section" className="mt-14">
            <h2 className="font-display text-xl md:text-2xl font-bold mb-6">Frequently asked questions</h2>
            <div className="space-y-4">
              {guide.faqs.map((f) => (
                <div key={f.q} className="border border-white/10 bg-[#111113] rounded-xl p-5">
                  <h3 className="font-display font-semibold text-base mb-2 text-white">{f.q}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-14 border border-[#A78BFA]/25 bg-[#A78BFA]/5 rounded-2xl p-8 text-center">
          <h3 className="font-display text-xl font-bold mb-2">Ready to scan your stack?</h3>
          <p className="text-sm text-zinc-400 mb-5">Film your cards once — CardArk identifies, prices and files every one into your digital binder.</p>
          {user ? (
            <Link to="/upload">
              <Button data-testid="guide-cta-upload-button" className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-full px-8 font-semibold transition-colors">
                Scan a video
              </Button>
            </Link>
          ) : (
            <Button data-testid="guide-cta-login-button" onClick={loginWithGoogle} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-full px-8 font-semibold transition-colors">
              Get started with Google
            </Button>
          )}
        </div>

        {related.length > 0 && (
          <div className="mt-14">
            <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-4">Keep reading</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {related.map((g) => (
                <Link
                  key={g.slug}
                  to={`/guides/${g.slug}`}
                  data-testid={`guide-related-${g.slug}`}
                  className="group border border-white/10 bg-[#111113] rounded-xl p-5 hover:border-[#A78BFA]/40 transition-colors"
                >
                  <p className="font-display font-semibold text-sm leading-snug mb-1 group-hover:text-[#A78BFA] transition-colors">{g.title}</p>
                  <span className="text-[10px] text-zinc-500 uppercase tracking-widest">{g.readTime}</span>
                  <ArrowRight className="w-3.5 h-3.5 text-[#A78BFA] mt-2" />
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
}
